import React, { useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  UploadFile,
  VerifiedUser,
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  Refresh,
  Warning,
} from '@mui/icons-material';
import { bulkImportService } from '../../services/bulkImport';
import { readExcelFile, downloadExcelFile } from '../../utils/importExport';

const steps = ['Upload File', 'Review & Validate', 'Upload Data'];

const BulkImport = () => {
  const history = useHistory();
  const location = useLocation();
  
  // Get module type from URL (defaults to 'customer')
  // API uses singular form: 'customer', 'lead', or 'siteVisit'
  const type = location.pathname.includes('/leads') 
    ? 'lead' 
    : location.pathname.includes('/siteVisit') 
      ? 'siteVisit' 
      : location.pathname.includes('/AssociateList')
        ? 'associate'
        : 'customer';
      
  const moduleName = type === 'lead' ? 'Leads' : type === 'siteVisit' ? 'Site Visits' : type === 'associate' ? 'Associates' : 'Customers';
  
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [validatedData, setValidatedData] = useState([]);
  const [backendValidationResult, setBackendValidationResult] = useState(null);
  const [validatingBackend, setValidatingBackend] = useState(false);
  
  const fileInputRef = useRef(null);

  const resetState = () => {
    setSelectedFile(null);
    setFileName('');
    setError('');
    setSuccess('');
    setValidationResult(null);
    setUploadResult(null);
    setParsedData([]);
    setValidatedData([]);
    setBackendValidationResult(null);
    setActiveStep(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Normalize row keys to standardized internal names
  const normalizeRow = (row) => {
    const normalized = {};
    const keyMap = {
      'name': ['name', 'Name', 'Name *', 'full name', 'Full Name', 'customer name', 'Customer Name'],
      'phone': ['phone', 'Phone', 'Phone *', 'mobile', 'Mobile', 'Mobile *', 'contact', 'Contact'],
      'email': ['email', 'Email', 'Email ID', 'email id', 'Email Address'],
      'whatsapp': ['whatsapp', 'WhatsApp', 'WhatsApp Number', 'whatsapp number', 'Whatsapp'],
      'company': ['company', 'Company', 'Company Name', 'Organization'],
      'profession': ['profession', 'Profession', 'designation', 'Designation', 'Occupation', 'Work'],
      'associatePhone': ['associatePhone', 'Associate Phone', 'Associate', 'associate phone', 'Associate Mobile'],
      'notes': ['notes', 'Notes', 'remark', 'Remark', 'remarks', 'Remarks', 'comment', 'Comment'],
      'city': ['city', 'City'],
      'state': ['state', 'State'],
      'address': ['address', 'Address', 'addressLine', 'Address Line', 'Location'],
      'pincode': ['pincode', 'Pincode', 'Zip Code', 'Zip'],
      'pan': ['pan', 'PAN', 'PAN Number', 'Pan Card'],
      'aadhar': ['aadhar', 'Aadhar', 'Aadhar Number', 'aadahr', 'Aadhaar'],
      'gender': ['gender', 'Gender'],
      'fatherName': ['fatherName', 'Father Name', 'Father/Husband Name', 'FatherHusbandName'],
      'bloodGroup': ['bloodGroup', 'Blood Group'],
      'dob': ['dob', 'Date of Birth', 'DOB', 'Birthday'],
      'age': ['age', 'Age'],
      'source': ['source', 'Source'],
      'status': ['status', 'Status'],
      'interestedIn': ['interestedIn', 'Interested In', 'Project', 'Requirement'],
      'budget': ['budget', 'Budget', 'budgetUpto'],
      'visitNo': ['visitNo', 'Visit No', 'Visit Number', 'visit no', 'visit number'],
      'leadNo': ['leadNo', 'Lead No', 'Lead Number', 'lead no', 'lead number'],
      'projectInterested': ['projectInterested', 'Project Interested', 'Project Name', 'project'],
      'visitRemark': ['visitRemark', 'Visit Remark', 'Visit Note', 'remark', 'Remark'],
      'nextFollowUpDate': ['nextFollowUpDate', 'Next Follow Up Date', 'Follow Up', 'Next FollowUp'],
      'visitedBy': ['visitedBy', 'Visited By', 'VisitedBy', 'Attended By'],
      'sponsorCode': ['sponsorCode', 'Sponsor Code', 'Sponsor', 'sponsor', 'sponsor id', 'Sponsor ID', 'Sponsor Code *', 'Sponsor ID *', 'Sponsor Login ID'],
      'registrationDate': ['registrationDate', 'Registration Date', 'date', 'Date of Registration', 'Registration Date *'],
      'commission': ['commission', 'Commission', 'Commission (%)', 'Comm', 'Commission *'],
      'designation': ['designation', 'Designation', 'Rank', 'Designation Name', 'Designation *'],
      'bankName': ['bankName', 'Bank Name', 'Bank', 'Bank Name *'],
      'accountNumber': ['accountNumber', 'Account Number', 'Account No', 'Account Number *'],
      'ifscCode': ['ifscCode', 'IFSC Code', 'IFSC', 'IFSC Code *'],
      'nomineeName': ['nomineeName', 'Nominee Name', 'Nominee'],
      'nomineePhone': ['nomineePhone', 'Nominee Phone', 'Nominee Mobile'],
      'nomineeRelation': ['nomineeRelation', 'Nominee Relation', 'Relation'],
      'role': ['role', 'Role', 'Access Role', 'User Role']
    };

    // Iterate through our standardized keys
    Object.keys(keyMap).forEach(stdKey => {
      const variations = keyMap[stdKey];
      // Find the first variation that exists in the row
      const actualKey = Object.keys(row).find(k => 
        variations.some(v => v.toLowerCase().trim() === k.toLowerCase().trim())
      );
      
      if (actualKey !== undefined) {
        normalized[stdKey] = row[actualKey];
      } else {
        normalized[stdKey] = '';
      }
    });

    // Also keep any original keys that didn't match our standardization
    Object.keys(row).forEach(k => {
      const isStd = Object.values(keyMap).some(variations => 
        variations.some(v => v.toLowerCase().trim() === k.toLowerCase().trim())
      );
      if (!isStd) {
        normalized[k] = row[k];
      }
    });

    return normalized;
  };

  // Helper to convert Excel date serial (e.g. 46106) to JS Date
  const excelDateToJS = (serial) => {
    if (!serial || isNaN(serial)) return null;
    const date = new Date((serial - 25569) * 86400 * 1000);
    return isNaN(date.getTime()) ? null : date;
  };

  // Transform flat normalized row to nested backend schema
  const transformRow = (row) => {
    // For lead and customer, we keep the check to prevent double nesting
    if (type === 'lead' && row.customerDetails) return row;
    if (type === 'customer' && row.personalDetails) return row;
    // We remove the siteVisit guard to ensure fresh transformation


    if (type === 'customer') {
      return {
        name: String(row.name || '').trim(),
        profession: String(row.profession || '').trim(),
        phone: String(row.phone || '').trim(),
        whatsapp: String(row.whatsapp || row.phone || '').trim(),
        email: row.email ? String(row.email).trim() : null,
        city: String(row.city || '').trim(),
        state: String(row.state || '').trim(),
        company: String(row.company || '').trim(),
        basicDetails: {
          gender: String(row.gender || '').trim(),
          fatherName: String(row.fatherName || '').trim(),
          bloodGroup: String(row.bloodGroup || '').trim(),
        },
        personalDetails: {
          dob: String(row.dob || '').trim(),
          age: String(row.age || '').trim(),
          anniversary: String(row.anniversary || '').trim(),
          pan: String(row.pan || '').trim(),
          aadahr: String(row.aadhar || '').trim(),
        },
        otherDetails: {
          address: String(row.address || '').trim(),
          createdDetail: String(row.createdDetail || '').trim(),
          remark: String(row.notes || '').trim(),
          salaryRange: String(row.salaryRange || '').trim(),
        },
        associatePhone: String(row.associatePhone || '').trim(),
        createdBy: row.createdBy ? String(row.createdBy).trim() : undefined,
      };
    } else if (type === 'sitevisit' || type === 'siteVisit' || type === 'site-visit') {
      // Hyper-resilient transformation matching user's EXACT Excel columns + nested CRM requirements
      const name = String(row.name || row.customer || row.customerName || row.Name || '').trim();
      const phone = String(row.phone || row.mobileNumber || row.mobile || row.contact || row.Phone || '').trim();
      const remark = String(row.visitRemark || row['Visit Remark'] || row.notes || row.visitDescription || '').trim();
      const email = String(row.email || row.Email || '').trim();
      const now = new Date();
      
      const visitDateISO = now.toISOString();
      const visitTime = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      let nextFD = null;
      const rawNextFD = row.nextFollowUpDate || row['Next Follow Up Date'] || row.followUp?.nextFollowUpDate;
      if (rawNextFD) {
        if (!isNaN(rawNextFD)) {
          const d = excelDateToJS(Number(rawNextFD));
          if (d) nextFD = d.toISOString();
        } else {
          try {
            const d = new Date(rawNextFD);
            if (!isNaN(d.getTime())) nextFD = d.toISOString();
          } catch (e) {}
        }
      }
      
      if (!nextFD) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        nextFD = tomorrow.toISOString();
      }

      const statusVal = String(row.status || row.Status || row.inquiryStatus || 'HOT').trim();

      // The final payload sent to the backend
      const payload = {
        // ROOR LEVEL (Site Visit Schema)
        customer: name,
        mobileNumber: phone,
        name: name,
        phone: phone,
        whatsappNumber: String(row.whatsapp || row.WhatsApp || phone || '').trim(),
        address: String(row.address || row.Address || '').trim(),
        source: String(row.source || row.Source || 'Bulk Import').trim(),
        interestedIn: String(row.interestedIn || row['Interested In'] || '').trim(),
        budgetUpto: String(row.budget || row.Budget || '').trim(),
        visitDate: visitDateISO,
        visitTime: visitTime,
        projects: Array.isArray(row.projects) ? row.projects : [],
        projectInterested: String(row.projectInterested || row['Project Interested'] || '').trim(),
        location: String(row.location || row.City || row.city || '').trim(),
        city: String(row.city || row.City || '').trim(),
        state: String(row.state || row.State || '').trim(),
        sizeArea: String(row.sizeArea || '').trim(),
        siteVisitBy: String(row.visitedBy || row['Visited By'] || row.siteVisitBy || 'Admin').trim(),
        siteVisitByPhone: String(row.associatePhone || row['Associate Phone'] || '').trim(),
        associatePhone: String(row.associatePhone || row['Associate Phone'] || '').trim(),
        broker: String(row.broker || '').trim(),
        visitDescription: remark,
        remark1: remark,
        inquiryStatus: statusVal,
        status: statusVal,
        nextFollowUpDate: nextFD,
        followUpRemark: remark,
        createdBy: row.createdBy ? String(row.createdBy).trim() : 'Admin User',
        visitCreatedOn: visitDateISO,
        
        // NESTED STRUCTURES (Lead/Customer Schema Compatibility)
        customerDetails: {
          name: name,
          phone: phone,
          mobile: phone,
          email: email || null,
          whatsapp: String(row.whatsapp || row.WhatsApp || phone || '').trim(),
          profession: String(row.profession || row.Profession || '').trim(),
        },
        customerInfo: {
          name: name,
          phone: phone,
          email: email || null,
          whatsapp: String(row.whatsapp || row.WhatsApp || phone || '').trim(),
          profession: String(row.profession || row.Profession || '').trim(),
        },
        personalDetails: {
          dob: row.dob || '',
          gender: row.gender || '',
        },
        otherDetails: {
          address: String(row.address || row.Address || '').trim(),
          remark: remark,
        }
      };

      console.log('Final Prepared Payload:', payload);
      return payload;
    } else if (type === 'associate') {
      const name = String(row.name || '').trim();
      // Generate login credentials if not provided
      const loginId = row.loginId || (name.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 4) + Math.floor(1000 + Math.random() * 9000));
      const password = row.password || (name.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 4) + '@' + Math.floor(1000 + Math.random() * 9000));
      
      return {
        loginId,
        password,
        role: (row.role || 'associate').toLowerCase(),
        modules: [],
        img: "",
        sponsorId: String(row.sponsorCode || '').trim(),
        name: name,
        phone: String(row.phone || '').trim(),
        email: row.email ? String(row.email).trim() : null,
        designation: String(row.designation || '').trim(),
        city: String(row.city || '').trim(),
        state: String(row.state || '').trim(),
        commission: Number(row.commission || 0),
        whatsapp: String(row.whatsapp || row.phone || '').trim(),
        address: String(row.address || '').trim(),
        pinCode: String(row.pincode || '').trim(),
        extraAmount: parseFloat(row.oldBusiness || 0),
        oldBookingAmount: String(row.oldBusiness || ''),
        
        sponsorDetail: [
          {
            sponsorName: String(row.sponsorName || row.sponsorCode || '').trim(),
            designation: String(row.sponsorDesignation || '').trim(),
            idCard: String(row.sponsorIdCard || '').trim(),
          },
        ],
        
        registrationDetail: [
          {
            registrationDate: row.registrationDate ? new Date(row.registrationDate).toISOString() : new Date().toISOString(),
            deActivationDate: row.deActivationDate ? new Date(row.deActivationDate).toISOString() : null,
          },
        ],
        
        otherDetail: [
          {
            employeeType: row.employeeType || "Permanent",
            department: row.department || "Sales",
            trialLastDate: row.trialLastDate ? new Date(row.trialLastDate).toISOString() : null,
          },
        ],
        
        personalDetail: [
          {
            fatherName: String(row.fatherName || '').trim(),
            motherName: String(row.motherName || '').trim(),
            dob: row.dob ? new Date(row.dob).toISOString() : null,
            anniversary: row.anniversary ? new Date(row.anniversary).toISOString() : null,
            gender: row.gender || "",
            marriedStatus: row.marriedStatus || "",
            qualification: row.qualification || "",
            aadhar: String(row.aadhar || '').trim(),
            pan: String(row.pan || '').trim(),
            AddressType: row.addressType || "",
            addressNumber: String(row.addressNumber || '').trim(),
            addressImage: "",
            panImage: "",
          },
        ],
        
        bankDetail: [
          {
            accountName: String(row.accountName || row.name || '').trim(),
            bank: String(row.bankName || '').trim(),
            accountNumber: String(row.accountNumber || '').trim(),
            ifsc: String(row.ifscCode || '').trim(),
            branch: String(row.branch || '').trim(),
            bankDocFile: "",
          },
        ],
        
        nomineeDetail: [
          {
            name: String(row.nomineeName || '').trim(),
            phone: String(row.nomineePhone || '').trim(),
            relation: String(row.nomineeRelation || '').trim(),
          },
        ],
        
        status: true,
        isActive: true,
        createdBy: "Admin",
      };
    } else {
      // Lead structure
      return {
        customerDetails: {
          name: String(row.name || '').trim(),
          phone: String(row.phone || '').trim(),
          email: row.email ? String(row.email).trim() : null,
          whatsapp: String(row.whatsapp || row.phone || '').trim(),
          company: String(row.company || '').trim(),
          designation: String(row.profession || '').trim(),
        },
        source: String(row.source || 'Bulk Import').trim(),
        status: String(row.status || 'New').trim(),
        interestedIn: String(row.interestedIn || '').trim(),
        budget: String(row.budget || '').trim(),
        address: {
          addressLine: String(row.address || '').trim(),
          city: String(row.city || '').trim(),
          state: String(row.state || '').trim(),
        },
        associatePhone: String(row.associatePhone || '').trim(),
        createdBy: row.createdBy ? String(row.createdBy).trim() : undefined,
        remarks: {
          generalRemark: String(row.notes || '').trim()
        }
      };
    }
  };

  // Validate customer data against schema
  const validateCustomerRow = (row, index) => {
    const errors = [];
    const warnings = [];

    // Required fields
    if (!row.name || !String(row.name).trim()) {
      errors.push('Name is required');
    }

    if (!row.phone || !String(row.phone).trim()) {
      errors.push('Phone is required');
    } else {
      const phoneStr = String(row.phone).replace(/\D/g, '');
      if (phoneStr.length !== 10 || !/^[4-9]/.test(phoneStr)) {
        errors.push('Phone must be 10 digits and cannot start with 0, 1, 2, or 3');
      }
    }

    // Email validation (optional but must be valid if provided)
    if (row.email && String(row.email).trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row.email)) {
        errors.push('Invalid email format');
      }
    }

    // WhatsApp validation (optional but must be valid if provided)
    if (row.whatsapp && String(row.whatsapp).trim()) {
      const whatsappStr = String(row.whatsapp).replace(/\D/g, '');
      if (whatsappStr.length !== 10 || !/^[4-9]/.test(whatsappStr)) {
        warnings.push('WhatsApp number should be 10 digits');
      }
    }

    // PAN validation (optional but must be valid if provided)
    if (row.pan && String(row.pan).trim()) {
      const panStr = String(row.pan).toUpperCase().trim();
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
      if (panStr.length !== 10) {
        errors.push('PAN must be exactly 10 characters');
      } else if (!panRegex.test(panStr)) {
        errors.push('Invalid PAN format. Must be AAAAA9999A (5 letters, 4 digits, 1 letter)');
      }
    }

    return {
      rowIndex: index,
      data: row,
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  // Validate lead data (standardized with customer validation)
  const validateLeadRow = (row, index) => {
    const errors = [];
    const warnings = [];

    // Required fields
    if (!row.name || !String(row.name).trim()) {
      errors.push('Name is required');
    }

    if (!row.phone || !String(row.phone).trim()) {
      errors.push('Phone is required');
    } else {
      const phoneStr = String(row.phone).replace(/\D/g, '');
      if (phoneStr.length !== 10 || !/^[4-9]/.test(phoneStr)) {
        errors.push('Phone must be 10 digits and cannot start with 0, 1, 2, or 3');
      }
    }

    // Email validation (optional but must be valid if provided)
    if (row.email && String(row.email).trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row.email)) {
        errors.push('Invalid email format');
      }
    }

    // WhatsApp validation (optional but must be valid if provided)
    if (row.whatsapp && String(row.whatsapp).trim()) {
      const whatsappStr = String(row.whatsapp).replace(/\D/g, '');
      if (whatsappStr.length !== 10 || !/^[4-9]/.test(whatsappStr)) {
        warnings.push('WhatsApp number should be 10 digits');
      }
    }

    return {
      rowIndex: index,
      data: row,
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  // Validate Site Visit data
  const validateSiteVisitRow = (row, index) => {
    const errors = [];
    const warnings = [];

    // Required fields
    if (!row.name || !String(row.name).trim()) {
      errors.push('Name is required');
    }

    if (!row.phone || !String(row.phone).trim()) {
      errors.push('Phone is required');
    } else {
      const phoneStr = String(row.phone).replace(/\D/g, '');
      if (phoneStr.length !== 10 || !/^[4-9]/.test(phoneStr)) {
        errors.push('Phone must be 10 digits and cannot start with 0, 1, 2, or 3');
      }
    }

    // Email validation (optional)
    if (row.email && String(row.email).trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row.email)) {
        errors.push('Invalid email format');
      }
    }

    return {
      rowIndex: index,
      data: row,
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  // Validate Associate data
  const validateAssociateRow = (row, index) => {
    const errors = [];
    const warnings = [];

    // Required fields
    if (!row.name || !String(row.name).trim()) {
      errors.push('Name is required');
    }

    if (!row.phone || !String(row.phone).trim()) {
      errors.push('Phone is required');
    } else {
      const phoneStr = String(row.phone).replace(/\D/g, '');
      if (phoneStr.length !== 10 || !/^[4-9]/.test(phoneStr)) {
        errors.push('Phone must be 10 digits and cannot start with 0, 1, 2, or 3');
      }
    }

    if (!row.sponsorCode || !String(row.sponsorCode).trim() || String(row.sponsorCode).trim() === '-') {
      errors.push('Sponsor Code/ID is required');
    }

    if (row.email && String(row.email).trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row.email)) {
        errors.push('Invalid email format');
      }
    }

    // PAN validation
    if (row.pan && String(row.pan).trim()) {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
      if (!panRegex.test(String(row.pan).toUpperCase().trim())) {
        errors.push('Invalid PAN format');
      }
    }

    return {
      rowIndex: index,
      data: row,
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  const handleDownloadSample = () => {
    setDownloadProgress(true);
    try {
      let sampleData = [];
      let fileName = '';

      if (type === 'customer') {
        sampleData = [
          {
            'Name *': 'John Doe',
            'Phone *': '9876543210',
            'WhatsApp': '9876543210',
            'Email': 'john@example.com',
            'Profession': 'Engineer',
            'Company': 'Tech Corp',
            'Gender': 'Male',
            'Father Name': 'Richard Doe',
            'Blood Group': 'O+',
            'Date of Birth': '1990-01-01',
            'PAN': 'ABCDE1234F',
            'Aadhar': '123456789012',
            'Address': '123 Main St',
            'City': 'Mumbai',
            'State': 'Maharashtra',
            'Associate Phone': '9000000000',
            'Notes': 'Sample note for customer import'
          }
        ];
        fileName = 'customer_import_sample.xlsx';
      } else if (type === 'lead') {
        sampleData = [
          {
            'Name *': 'Jane Smith',
            'Phone *': '9123456789',
            'WhatsApp': '9123456789',
            'Email': 'jane@example.com',
            'Company': 'Real Estate Inc',
            'Designation': 'Manager',
            'Source': 'Google Ads',
            'Status': 'New',
            'Interested In': 'Project Green Valley',
            'Budget': '5000000',
            'Address': '456 Park Ave',
            'City': 'Delhi',
            'State': 'Delhi',
            'Associate Phone': '9000000000',
            'Notes': 'Interested in 2BHK plots'
          }
        ];
        fileName = 'lead_import_sample.xlsx';
      } else if (type === 'sitevisit' || type === 'siteVisit') {
        sampleData = [
          {
            'Name *': 'Robert Brown',
            'Phone *': '8887776665',
            'WhatsApp': '8887776665',
            'Email': 'robert@example.com',
            'Profession': 'Architect',
            'Project Interested': 'Project Highlands',
            'Interested In': '3 BHK Villa',
            'Budget': '15000000',
            'Visit Remark': 'Very interested, wants to book soon',
            'Visited By': 'Deepak Sharma',
            'Next Follow Up Date': '2026-03-25',
            'Status': 'HOT',
            'Address': '789 Skyline Road',
            'City': 'Pune',
            'State': 'Maharashtra',
            'Associate Phone': '9000000000',
            'Notes': 'Site visit completed'
          }
        ];
        fileName = 'sitevisit_import_sample.xlsx';
      } else if (type === 'associate') {
        sampleData = [
          {
            'Name *': 'Rajesh Kumar',
            'Phone *': '9876543210',
            'Sponsor Code *': 'AS123456',
            'Email': 'rajesh@example.com',
            'Designation': 'Associate',
            'Commission (%)': '5',
            'Aadhar': '123456789012',
            'PAN': 'ABCDE1234F',
            'Bank Name': 'HDFC Bank',
            'Account Number': '1234567890',
            'IFSC Code': 'HDFC0001234',
            'City': 'Mumbai',
            'State': 'Maharashtra',
            'Father Name': 'Suresh Kumar',
            'Nominee Name': 'Sunita Kumar',
            'Relation': 'Wife'
          }
        ];
        fileName = 'associate_import_sample.xlsx';
      }

      downloadExcelFile({
        rows: sampleData,
        sheetName: 'Sample Data',
        fileName: fileName
      });

      setSuccess('Sample file generated and downloaded successfully!');
    } catch (err) {
      setError('Failed to generate sample file: ' + err.message);
    } finally {
      setDownloadProgress(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        setError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      
      setLoading(true);
      setError('');
      setSuccess('');
      setValidationResult(null);
      setUploadResult(null);
      setSelectedFile(file);
      setFileName(file.name);

      try {
        // Read Excel file
        const rows = await readExcelFile(file);
        
        if (!Array.isArray(rows) || rows.length === 0) {
          throw new Error('Excel file is empty or invalid');
        }

        // Normalize rows (standardize keys)
        const normalizedRows = rows.map(r => normalizeRow(r));
        
        // Validate each row
        const validateFn = type === 'customer' 
          ? validateCustomerRow 
          : type === 'lead' 
            ? validateLeadRow 
            : type === 'associate'
              ? validateAssociateRow
              : validateSiteVisitRow;
        const validated = normalizedRows.map((row, index) => validateFn(row, index + 1));
        
        setParsedData(normalizedRows);
        setValidatedData(validated);
        setActiveStep(1);

        // Calculate summary
        const validCount = validated.filter(v => v.isValid).length;
        const invalidCount = validated.length - validCount;
        const totalErrors = validated.reduce((sum, v) => sum + v.errors.length, 0);
        const totalWarnings = validated.reduce((sum, v) => sum + v.warnings.length, 0);

        setValidationResult({
          total: validated.length,
          valid: validCount,
          invalid: invalidCount,
          totalErrors,
          totalWarnings,
        });

        if (validCount > 0) {
          setSuccess(`File parsed successfully! ${validCount} valid record(s) found.`);
        } else {
          setError('No valid records found. Please check your file and try again.');
        }
      } catch (err) {
        setError(err.message || 'Failed to read Excel file');
        setSelectedFile(null);
        setFileName('');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReUpload = () => {
    resetState();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleBackendValidation = async () => {
    const validRecords = validatedData.filter(v => v.isValid);
    
    if (validRecords.length === 0) {
      setError('No valid records to validate. Please fix the frontend validation errors first.');
      return;
    }

    setValidatingBackend(true);
    setError('');
    setSuccess('');
    setBackendValidationResult(null);

    try {
      // Prepare data array for backend validation (send raw data, not transformed)
      const dataToValidate = validRecords.map(v => v.data);

      // Use 'site-visit' hyphenated for backend API consistency
      const apiType = type === 'siteVisit' || type === 'sitevisit' ? 'site-visit' : type;
      const result = await bulkImportService.validateData(apiType, dataToValidate);
      setBackendValidationResult(result);

      if (result?.isValid) {
        setSuccess(result?.message || `Backend validation passed! ${result.validRows || 0} valid record(s) ready to upload.`);
        // Don't change step - stay on validation step to show upload button
      } else {
        if (result?.validRows > 0) {
          setSuccess(result?.message || `Backend validation completed. ${result.validRows} valid record(s) can be uploaded.`);
        } else {
          setError(result?.message || 'Backend validation failed. No valid records found.');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to validate data with backend');
    } finally {
      setValidatingBackend(false);
    }
  };

  const handleUpload = async () => {
    let rawValidData = [];
    
    // Collect the valid raw records from either backend or frontend validation
    if (backendValidationResult?.sampleValidData && backendValidationResult.sampleValidData.length > 0) {
      rawValidData = backendValidationResult.sampleValidData
        .filter(item => item.isValid)
        .map(item => item.data);
    } else {
      const validRecords = validatedData.filter(v => v.isValid);
      rawValidData = validRecords.map(v => v.data);
    }

    if (rawValidData.length === 0) {
      setError('No valid records to upload. Please fix the errors and try again.');
      return;
    }

    // Transform ALL records to ensure consistent nested structure
    const dataToUpload = rawValidData.map(row => transformRow(row));

    // Get current user from localStorage or context
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const createdBy = user?.name || user?.email || 'Admin User';

    setLoading(true);
    setError('');
    setSuccess('');
    setUploadResult(null);
    setActiveStep(2);

    try {
      // Use 'site-visit' hyphenated to match the API route convention and validation type
      const apiType = type === 'siteVisit' || type === 'sitevisit' || type === 'site-visit' ? 'site-visit' : type;
      const result = await bulkImportService.uploadFile(apiType, dataToUpload, createdBy);
      setUploadResult(result);
      
      // Check if upload was successful and records were actually added
      if ((result?.status === true || result?.success === true) && (result?.count > 0 || result?.data?.count > 0)) {
        setSuccess(result?.message || `Successfully uploaded ${result?.count || result?.data?.count || dataToUpload.length} ${moduleName.toLowerCase()} record(s)!`);
        // Reset after 3 seconds and redirect only if there was actual success
        setTimeout(() => {
          resetState();
          history.goBack();
        }, 3000);
      } else if (result?.status === true || result?.success === true) {
        // Status is true but count is 0 - likely failed records but API returned OK
        if (result?.errors && result.errors.length > 0) {
          setError(result?.message || 'No records were uploaded. Please check the errors below.');
        } else {
          setSuccess(result?.message || 'Upload process completed, but no new records were added (they may be duplicates).');
        }
      } else {
        // This should not happen as errors are thrown, but handle it just in case
        setError(result?.message || 'Upload failed. Please check the errors and try again.');
        setActiveStep(1); // Go back to validation step on error
      }
    } catch (err) {
      setError(err.message || 'Failed to upload file');
      setActiveStep(1); // Go back to validation step on error
      setUploadResult({
        success: false,
        status: false,
        message: err.message || 'Upload failed',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <UploadFile sx={{ fontSize: 64, color: '#536DFE', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Upload Your Excel File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select the Excel file you want to import. Make sure it follows the required format.
              </Typography>
              <Button
                variant="outlined"
                startIcon={downloadProgress ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleDownloadSample}
                disabled={downloadProgress}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  mb: 2,
                  borderColor: '#536DFE',
                  color: '#536DFE',
                  '&:hover': {
                    borderColor: '#3f51b5',
                    backgroundColor: 'rgba(83, 109, 254, 0.04)',
                  },
                }}
              >
                {downloadProgress ? 'Downloading...' : 'Download Sample File (Optional)'}
              </Button>
            </Box>
            
            <Card
              variant="outlined"
              sx={{
                border: '2px dashed',
                borderColor: selectedFile ? '#4caf50' : '#e0e0e0',
                backgroundColor: selectedFile ? '#f1f8f4' : '#fafafa',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: '#536DFE',
                  backgroundColor: '#f5f5f5',
                },
              }}
              onClick={handleBrowse}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <CloudUpload sx={{ fontSize: 48, color: selectedFile ? '#4caf50' : '#9e9e9e', mb: 2 }} />
                {selectedFile ? (
                  <>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#4caf50' }}>
                      {fileName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      File selected. Click to change file.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      Click to browse or drag and drop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supported formats: .xlsx, .xls
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ py: 4 }}>
            {/* Summary Card */}
            {validationResult && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#536DFE' }}>
                          {validationResult.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Records
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f1f8f4', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                          {validationResult.valid}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Valid Records
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#ffebee', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#f44336' }}>
                          {validationResult.invalid}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Invalid Records
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                          {validationResult.totalErrors}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Errors
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Data Table */}
            {validatedData.length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Validation Results
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleReUpload}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Re-upload File
                    </Button>
                  </Box>
                  
                  <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Row</TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Phone</TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Associate Phone</TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Email</TableCell>
                          {type === 'customer' && (
                            <>
                              <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>City</TableCell>
                              <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>State</TableCell>
                            </>
                          )}
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Errors/Warnings</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validatedData.map((item, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor: item.isValid ? '#f1f8f4' : '#ffebee',
                              '&:hover': {
                                backgroundColor: item.isValid ? '#e8f5e9' : '#ffcdd2',
                              },
                            }}
                          >
                            <TableCell>{item.rowIndex}</TableCell>
                            <TableCell>
                              {item.isValid ? (
                                <Tooltip title="Valid">
                                  <ValidIcon sx={{ color: '#4caf50' }} />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Invalid">
                                  <InvalidIcon sx={{ color: '#f44336' }} />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>{item.data.name || '-'}</TableCell>
                            <TableCell>{item.data.phone || '-'}</TableCell>
                            <TableCell>{item.data.associatePhone || '-'}</TableCell>
                            <TableCell>{item.data.email || '-'}</TableCell>
                            {type === 'customer' && (
                              <>
                                <TableCell>{item.data.city || '-'}</TableCell>
                                <TableCell>{item.data.state || '-'}</TableCell>
                              </>
                            )}
                            <TableCell>
                              <Box>
                                {item.errors.length > 0 && (
                                  <Box sx={{ mb: 0.5 }}>
                                    {item.errors.map((err, i) => (
                                      <Chip
                                        key={i}
                                        label={err}
                                        size="small"
                                        color="error"
                                        sx={{ fontSize: '10px', height: '20px', mr: 0.5, mb: 0.5 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                                {item.warnings.length > 0 && (
                                  <Box>
                                    {item.warnings.map((warn, i) => (
                                      <Chip
                                        key={i}
                                        label={warn}
                                        size="small"
                                        color="warning"
                                        sx={{ fontSize: '10px', height: '20px', mr: 0.5, mb: 0.5 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                                {item.errors.length === 0 && item.warnings.length === 0 && (
                                  <Chip
                                    label="No issues"
                                    size="small"
                                    color="success"
                                    sx={{ fontSize: '10px', height: '20px' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Backend Validation Results */}
            {backendValidationResult && (
              <Card variant="outlined" sx={{ mb: 3, mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Backend Validation Results
                  </Typography>
                  
                  {backendValidationResult.isValid ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {backendValidationResult.message || 'Backend validation passed!'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {backendValidationResult.validRows} valid row(s) ready to upload
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {backendValidationResult.message || 'Backend validation failed'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {backendValidationResult.invalidRows} invalid row(s) found
                      </Typography>
                    </Alert>
                  )}

                  {backendValidationResult.errors && backendValidationResult.errors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Backend Errors ({backendValidationResult.errors.length}):
                      </Typography>
                      {backendValidationResult.errors.map((error, index) => (
                        <Alert key={index} severity="error" sx={{ mb: 1 }}>
                          {error}
                        </Alert>
                      ))}
                    </Box>
                  )}

                  {backendValidationResult.warnings && backendValidationResult.warnings.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Backend Warnings ({backendValidationResult.warnings.length}):
                      </Typography>
                      {backendValidationResult.warnings.map((warning, index) => (
                        <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                          {warning}
                        </Alert>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleReUpload}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Re-upload File
              </Button>
              
              {validationResult && validationResult.valid > 0 && !backendValidationResult && (
                <Button
                  variant="contained"
                  startIcon={validatingBackend ? <CircularProgress size={20} color="inherit" /> : <VerifiedUser />}
                  onClick={handleBackendValidation}
                  disabled={validatingBackend}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    backgroundColor: '#4caf50',
                    '&:hover': {
                      backgroundColor: '#388e3c',
                    },
                  }}
                >
                  {validatingBackend ? 'Validating...' : 'Validate with Backend'}
                </Button>
              )}

              {backendValidationResult && (
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                  onClick={handleUpload}
                  disabled={loading || (backendValidationResult.validRows === 0 && (!backendValidationResult.sampleValidData || backendValidationResult.sampleValidData.length === 0))}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    backgroundColor: backendValidationResult.isValid ? '#536DFE' : '#ff9800',
                    '&:hover': {
                      backgroundColor: backendValidationResult.isValid ? '#3f51b5' : '#f57c00',
                    },
                  }}
                >
                  {loading 
                    ? 'Uploading...' 
                    : `Upload ${backendValidationResult.validRows || (backendValidationResult.sampleValidData?.filter(item => item.isValid).length || 0) || validationResult.valid} Valid Record(s)`
                  }
                </Button>
              )}
            </Box>
          </Box>
        );
      
      case 2:
        if (loading && !uploadResult) {
          return (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Uploading Data...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please do not refresh the page or go back.
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {(uploadResult?.status === true || uploadResult?.success === true) ? (
                (uploadResult.count > 0 || uploadResult.data?.count > 0) ? (
                  uploadResult.errors?.length > 0 ? (
                    <>
                      <Warning sx={{ fontSize: 64, color: '#ff9800', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ff9800' }}>
                        Partial Success
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4caf50' }}>
                        Upload Successful!
                      </Typography>
                    </>
                  )
                ) : (
                  <>
                    <Warning sx={{ fontSize: 64, color: '#ff9800', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ff9800' }}>
                      No Records Uploaded
                    </Typography>
                  </>
                )
              ) : (
                <>
                  <ErrorIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#f44336' }}>
                    Upload Failed
                  </Typography>
                </>
              )}
            </Box>

            {uploadResult && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  {(uploadResult.status === true || uploadResult.success === true) ? (
                    (uploadResult.count > 0 || uploadResult.data?.count > 0) ? (
                      <Alert severity={uploadResult.errors?.length > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {uploadResult.message || `${uploadResult.count || uploadResult.data?.count || 0} ${moduleName.toLowerCase()} record(s) processed.`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {uploadResult.count || uploadResult.data?.count} record(s) uploaded successfully.
                          {uploadResult.errors?.length > 0 && ` ${uploadResult.errors.length} record(s) failed.`}
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {uploadResult.message || 'No records were uploaded.'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Processing completed but 0 records were added to the database. Check the errors or duplicate data.
                        </Typography>
                      </Alert>
                    )
                  ) : (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Upload Failed
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {uploadResult.message || 'An error occurred during upload.'}
                      </Typography>
                    </Alert>
                  )}

                  {(uploadResult.errors || uploadResult.data?.errors) && (uploadResult.errors?.length > 0 || uploadResult.data?.errors?.length > 0) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Errors:
                      </Typography>
                      {(uploadResult.errors || uploadResult.data?.errors).map((error, index) => (
                        <Alert key={index} severity="error" sx={{ mb: 1 }}>
                          {typeof error === 'string' ? error : error.message || error.error || JSON.stringify(error)}
                        </Alert>
                      ))}
                    </Box>
                  )}

                  {uploadResult.summary && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Summary:
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(uploadResult.summary).map(([key, value]) => (
                          <Grid item xs={6} sm={4} key={key}>
                            <Chip
                              label={`${key}: ${value}`}
                              variant="outlined"
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <IconButton onClick={() => history.goBack()} sx={{ color: '#536DFE' }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              Bulk Import {moduleName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Import {moduleName.toLowerCase()} data in bulk using Excel file
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Card */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Divider sx={{ mb: 4 }} />

          {/* Loading Indicator */}
          {loading && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Processing...
              </Typography>
            </Box>
          )}

          {/* Step Content */}
          {getStepContent(activeStep)}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BulkImport;

